function getZoomAdjustedViewportWidth() {
    if (typeof window === 'undefined') {
        return 0;
    }

    const viewport = window.visualViewport;
    if (viewport && typeof viewport.width === 'number' && viewport.width > 0) {
        return Math.round(viewport.width);
    }

    const doc = document.documentElement;
    return window.innerWidth || (doc && doc.clientWidth) || 0;
}

/**
 * MobileProductTitleController
 *
 * On small mobile (<=649px) the breadcrumb lives at body-level with
 * position: sticky (set by the inline script in theme.liquid).  This
 * controller watches the first product image: once the image scrolls
 * past the breadcrumb, it hides the breadcrumb via the
 * `breadcrumb--past-image` CSS class.  No position changes are needed.
 */
class MobileProductTitleController {
    constructor() {
        this.mobileMaxWidth = 649;
        this.threshold = 16;
        this.container = document.querySelector('.custom-product-container');
        this.titleWrapper = document.querySelector('.product-title-bar .collection-hero__title-wrapper');
        this.updateQueued = false;
        this.isPastImage = false;

        if (!this.container || !this.titleWrapper) {
            return;
        }

        this.boundQueueUpdate = this.queueUpdate.bind(this);
        this.boundHandleResize = this.handleResize.bind(this);

        // Read the sticky-top value that theme.liquid wrote as an inline style,
        // falling back to the computed margin-top from the stylesheet.
        this.stickyTop = parseFloat(this.titleWrapper.style.top) ||
            parseFloat(window.getComputedStyle(this.titleWrapper).marginTop) || 0;

        this.observeImages();
        this.queueUpdate();

        window.addEventListener('scroll', this.boundQueueUpdate, { passive: true });
        window.addEventListener('resize', this.boundHandleResize, { passive: false });
        if (window.visualViewport) {
            window.visualViewport.addEventListener('resize', this.boundHandleResize, { passive: true });
        }
    }

    isMobile() {
        return getZoomAdjustedViewportWidth() <= this.mobileMaxWidth;
    }

    observeImages() {
        const images = this.container.querySelectorAll('img');
        images.forEach((img) => {
            if (!img.complete) {
                img.addEventListener('load', this.boundQueueUpdate, { once: true });
            }
        });
    }

    handleResize() {
        if (this.titleWrapper) {
            this.titleWrapper.classList.remove('breadcrumb--past-image');
            this.isPastImage = false;
            delete this.titleWrapper.dataset.mobileStickyState;

            // Re-read sticky top in case it changed
            this.stickyTop = parseFloat(this.titleWrapper.style.top) ||
                parseFloat(window.getComputedStyle(this.titleWrapper).marginTop) || 0;
        }

        this.observeImages();
        this.queueUpdate();
    }

    getReferenceTarget() {
        // First try mobile slider container
        const sliderContainer = document.querySelector('.mobile-slider-container');
        if (sliderContainer && sliderContainer.offsetHeight > 0) {
            return sliderContainer;
        }

        // Then try first mobile slide image
        const mobileSlideImage = document.querySelector('.mobile-image-slider .mobile-slide img');
        if (mobileSlideImage && mobileSlideImage.offsetHeight > 0) {
            return mobileSlideImage;
        }

        // Fallback to desktop gallery
        const gallery = document.querySelector('.custom-image-gallery');
        if (gallery && gallery.offsetHeight > 0) {
            const images = gallery.querySelectorAll('img');
            if (images.length) {
                return images[0];
            }
            return gallery;
        }

        return null;
    }

    queueUpdate() {
        if (this.updateQueued) {
            return;
        }

        this.updateQueued = true;
        requestAnimationFrame(() => {
            this.updateQueued = false;
            this.update();
        });
    }

    update() {
        if (!this.titleWrapper) {
            return;
        }

        if (!this.isMobile()) {
            this.resetStyles();
            return;
        }

        const referenceTarget = this.getReferenceTarget();
        if (!referenceTarget) {
            return;
        }

        // Get measurements
        const targetRect = referenceTarget.getBoundingClientRect();
        const titleHeight = this.titleWrapper.offsetHeight;

        // Where the breadcrumb bottom sits when in sticky position
        const stickyBottom = this.stickyTop + titleHeight;

        // Gap between image bottom and breadcrumb bottom
        const gap = targetRect.bottom - stickyBottom;

        if (gap <= this.threshold) {
            this.applyPastImage();
        } else {
            this.clearPastImage();
        }
    }

    applyPastImage() {
        if (this.isPastImage) {
            return;
        }
        this.titleWrapper.classList.add('breadcrumb--past-image');
        this.isPastImage = true;
        this.titleWrapper.dataset.mobileStickyState = 'past-image';
    }

    clearPastImage() {
        if (!this.isPastImage) {
            return;
        }
        this.titleWrapper.classList.remove('breadcrumb--past-image');
        this.isPastImage = false;
        delete this.titleWrapper.dataset.mobileStickyState;
    }

    resetStyles() {
        if (!this.isPastImage) {
            return;
        }
        this.titleWrapper.classList.remove('breadcrumb--past-image');
        delete this.titleWrapper.dataset.mobileStickyState;
        this.isPastImage = false;
    }

    destroy() {
        window.removeEventListener('scroll', this.boundQueueUpdate);
        window.removeEventListener('resize', this.boundHandleResize);
        if (window.visualViewport) {
            window.visualViewport.removeEventListener('resize', this.boundHandleResize);
        }
        this.resetStyles();
    }
}

// Handle responsive initialization
if (typeof window !== 'undefined') {
    (function () {
        let controllerInstance = null;
        let resizeTimeout = null;
        let lastKnownViewportState = null;
        const MOBILE_MAX_WIDTH = 649;

        function isMobileViewport() {
            return getZoomAdjustedViewportWidth() <= MOBILE_MAX_WIDTH;
        }

        function initController() {
            const container = document.querySelector('.custom-product-container');
            if (isMobileViewport() && container && !controllerInstance) {
                controllerInstance = new MobileProductTitleController();
                window.mobileProductTitleController = controllerInstance;
                lastKnownViewportState = 'mobile';
            }
        }

        function destroyController() {
            if (!isMobileViewport() && controllerInstance) {
                if (typeof controllerInstance.destroy === 'function') {
                    controllerInstance.destroy();
                }
                controllerInstance = null;
                window.mobileProductTitleController = null;
                lastKnownViewportState = 'desktop';
            }
        }

        function handleResize() {
            if (resizeTimeout) {
                clearTimeout(resizeTimeout);
            }

            resizeTimeout = setTimeout(() => {
                const isMobile = isMobileViewport();
                const currentState = isMobile ? 'mobile' : 'desktop';

                if (currentState !== lastKnownViewportState) {
                    if (isMobile) {
                        initController();
                    } else {
                        destroyController();
                    }
                } else if (controllerInstance && typeof controllerInstance.handleResize === 'function') {
                    controllerInstance.handleResize();
                }
            }, 250);
        }

        document.addEventListener('DOMContentLoaded', () => {
            initController();
            window.addEventListener('resize', handleResize, { passive: true });
            if (window.visualViewport) {
                window.visualViewport.addEventListener('resize', handleResize, { passive: true });
            }
        });
    })();
}
